import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { 
  Users, Shield, Hospital, Building2, Stethoscope, User, TrendingUp, 
  Activity, Calendar, UserCheck, Settings, UserPlus, BarChart3,
  Clock, CheckCircle, AlertCircle, Bell, PieChart as PieChartIcon
} from 'lucide-react';
import { BASE_URL } from '../config';

// Custom Pie Chart Component
const CustomPieChart = ({ data, title }) => {
  const total = data.reduce((sum, item) => sum + item.value, 0);
  let cumulativePercentage = 0;

  const createPath = (percentage, cumulativePercentage) => {
    const startAngle = cumulativePercentage * 3.6; // Convert to degrees
    const endAngle = (cumulativePercentage + percentage) * 3.6;
    
    const startAngleRad = (startAngle - 90) * (Math.PI / 180);
    const endAngleRad = (endAngle - 90) * (Math.PI / 180);
    
    const largeArcFlag = percentage > 50 ? 1 : 0;
    
    const x1 = 50 + 40 * Math.cos(startAngleRad);
    const y1 = 50 + 40 * Math.sin(startAngleRad);
    const x2 = 50 + 40 * Math.cos(endAngleRad);
    const y2 = 50 + 40 * Math.sin(endAngleRad);
    
    return `M 50 50 L ${x1} ${y1} A 40 40 0 ${largeArcFlag} 1 ${x2} ${y2} Z`;
  };

  return (
    <div className="bg-white rounded-lg p-6 shadow-sm border border-slate-200">
      <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
        <PieChartIcon className="w-5 h-5 text-medical-600" />
        {title}
      </h3>
      <div className="flex items-center gap-6">
        <div className="relative">
          <svg width="200" height="200" viewBox="0 0 100 100" className="transform -rotate-90">
            {data.map((item, index) => {
              const percentage = (item.value / total) * 100;
              const path = createPath(percentage, cumulativePercentage);
              cumulativePercentage += percentage;
              
              return (
                <path
                  key={index}
                  d={path}
                  fill={item.color}
                  stroke="white"
                  strokeWidth="0.5"
                  className="hover:opacity-80 transition-opacity cursor-pointer"
                />
              );
            })}
          </svg>
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center">
              <div className="text-2xl font-bold text-slate-900">{total}</div>
              <div className="text-xs text-slate-600">Total</div>
            </div>
          </div>
        </div>
        <div className="flex-1">
          <div className="space-y-2">
            {data.map((item, index) => (
              <div key={index} className="flex items-center gap-3">
                <div 
                  className="w-3 h-3 rounded-full" 
                  style={{ backgroundColor: item.color }}
                ></div>
                <span className="text-sm text-slate-700 flex-1">{item.label}</span>
                <span className="text-sm font-medium text-slate-900">{item.value}</span>
                <span className="text-xs text-slate-500">
                  ({((item.value / total) * 100).toFixed(1)}%)
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

// Custom Bar Chart Component
const CustomBarChart = ({ data, title }) => {
  const maxValue = Math.max(...data.map(item => item.value));

  return (
    <div className="bg-white rounded-lg p-6 shadow-sm border border-slate-200">
      <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
        <BarChart3 className="w-5 h-5 text-medical-600" />
        {title}
      </h3>
      <div className="space-y-4">
        {data.map((item, index) => (
          <div key={index} className="flex items-center gap-4">
            <div className="w-20 text-sm text-slate-700 font-medium truncate">
              {item.label}
            </div>
            <div className="flex-1 flex items-center gap-3">
              <div className="flex-1 bg-slate-100 rounded-full h-6 relative overflow-hidden">
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: `${(item.value / maxValue) * 100}%` }}
                  transition={{ duration: 1, delay: index * 0.1 }}
                  className="h-full rounded-full"
                  style={{ backgroundColor: item.color }}
                />
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="text-xs font-medium text-slate-700">
                    {item.value}
                  </span>
                </div>
              </div>
              <div className="w-12 text-right">
                <span className="text-xs text-slate-500">
                  {((item.value / maxValue) * 100).toFixed(0)}%
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// Custom Donut Chart for Analytics
const CustomDonutChart = ({ data, title, centerText }) => {
  const total = data.reduce((sum, item) => sum + item.value, 0);
  let cumulativePercentage = 0;

  const createDonutPath = (percentage, cumulativePercentage) => {
    const startAngle = cumulativePercentage * 3.6;
    const endAngle = (cumulativePercentage + percentage) * 3.6;
    
    const startAngleRad = (startAngle - 90) * (Math.PI / 180);
    const endAngleRad = (endAngle - 90) * (Math.PI / 180);
    
    const largeArcFlag = percentage > 50 ? 1 : 0;
    
    const outerRadius = 35;
    const innerRadius = 20;
    
    const x1 = 50 + outerRadius * Math.cos(startAngleRad);
    const y1 = 50 + outerRadius * Math.sin(startAngleRad);
    const x2 = 50 + outerRadius * Math.cos(endAngleRad);
    const y2 = 50 + outerRadius * Math.sin(endAngleRad);
    
    const x3 = 50 + innerRadius * Math.cos(endAngleRad);
    const y3 = 50 + innerRadius * Math.sin(endAngleRad);
    const x4 = 50 + innerRadius * Math.cos(startAngleRad);
    const y4 = 50 + innerRadius * Math.sin(startAngleRad);
    
    return `M ${x1} ${y1} A ${outerRadius} ${outerRadius} 0 ${largeArcFlag} 1 ${x2} ${y2} L ${x3} ${y3} A ${innerRadius} ${innerRadius} 0 ${largeArcFlag} 0 ${x4} ${y4} Z`;
  };

  return (
    <div className="flex items-center gap-4">
      <div className="relative">
        <svg width="120" height="120" viewBox="0 0 100 100">
          {data.map((item, index) => {
            const percentage = (item.value / total) * 100;
            const path = createDonutPath(percentage, cumulativePercentage);
            cumulativePercentage += percentage;
            
            return (
              <path
                key={index}
                d={path}
                fill={item.color}
                stroke="white"
                strokeWidth="1"
                className="hover:opacity-80 transition-opacity"
              />
            );
          })}
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center">
            <div className="text-lg font-bold text-slate-900">{centerText}</div>
          </div>
        </div>
      </div>
      <div className="flex-1 space-y-1">
        {data.map((item, index) => (
          <div key={index} className="flex items-center gap-2">
            <div 
              className="w-2 h-2 rounded-full" 
              style={{ backgroundColor: item.color }}
            ></div>
            <span className="text-xs text-slate-600">{item.label}</span>
            <span className="text-xs font-medium text-slate-900 ml-auto">{item.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default function SuperadminDashboard({ onNavigate }) {
  const [stats, setStats] = useState({
    superadmin: 0,
    admin: 0,
    hospital: 0,
    clinic: 0,
    doctor: 0,
    user: 0,
    total: 0,
  });
  const [analytics, setAnalytics] = useState({
    activeUsers: 0,
    newUsersToday: 0,
    totalSessions: 0,
    avgSessionTime: '0m',
    approvedDoctors: 0,
    pendingApprovals: 0,
    systemUptime: '99.9%',
    totalRevenue: 0
  });
  const [permissions, setPermissions] = useState({
    system_functions: {},
    sidebar_access: {}
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
    fetchAnalytics();
    fetchPermissions();
  }, []);

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      console.log('🔍 Token from localStorage:', token ? 'Token exists' : 'No token found');
      console.log('🔍 Making request to:', `${BASE_URL}/api/superadmin/stats-simple/`);
      
      // Try the simple endpoint first
      const res = await axios.get(`${BASE_URL}/api/superadmin/stats-simple/`);
      console.log('✅ Stats response:', res.data);
      setStats(res.data);
    } catch (err) {
      console.error('❌ Error fetching stats:', err);
      console.error('❌ Error response:', err.response?.data);
      console.error('❌ Error status:', err.response?.status);
      
      // Fallback to mock data if API fails
      setStats({
        superadmin: 1,
        admin: 2,
        hospital: 3,
        clinic: 4,
        doctor: 15,
        user: 50,
        total: 75
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchAnalytics = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      // Mock analytics data - replace with actual API call
      setAnalytics({
        activeUsers: Math.floor(stats.total * 0.7),
        newUsersToday: Math.floor(Math.random() * 50) + 10,
        totalSessions: Math.floor(stats.total * 2.3),
        avgSessionTime: '12m 34s',
        approvedDoctors: Math.floor(stats.doctor * 0.85),
        pendingApprovals: Math.floor(stats.doctor * 0.15),
        systemUptime: '99.9%',
        totalRevenue: Math.floor(Math.random() * 50000) + 25000
      });
    } catch (err) {
      console.error('Error fetching analytics:', err);
    }
  };

  const fetchPermissions = async () => {
    try {
      const userId = localStorage.getItem('userId');
      const userRole = localStorage.getItem('userRole');
      
      console.log('🔍 Fetching permissions for user:', userId, 'role:', userRole);
      console.log('🔍 localStorage contents:', {
        userId: localStorage.getItem('userId'),
        userRole: localStorage.getItem('userRole'),
        accessToken: localStorage.getItem('accessToken') ? 'exists' : 'missing'
      });
      
      // If superadmin (role 6), grant all permissions
      if (userRole === '6') {
        console.log('✅ Superadmin detected - granting all permissions');
        setPermissions({
          system_functions: {
            add_admin: true,
            setup: true,
            notifications: true,
            discounts: true,
          },
          sidebar_access: {
            dashboard: true,
            hospitals: true,
            clinics: true,
            doctors: true,
            users: true,
            packages: true,
            departments: true,
            seo: true,
          }
        });
        return;
      }
      
      // For admin (role 5), fetch from API
      if (userRole === '5' && userId) {
        console.log('🔍 Admin detected - fetching permissions from API');
        const res = await axios.get(`${BASE_URL}/api/superadmin/permissions/${userId}/`);
        console.log('✅ Permissions fetched:', res.data);
        console.log('✅ System functions:', res.data.system_functions);
        console.log('✅ Sidebar access:', res.data.sidebar_access);
        setPermissions({
          system_functions: res.data.system_functions || {},
          sidebar_access: res.data.sidebar_access || {}
        });
      } else {
        console.log('⚠️ No userId or not admin role - setting empty permissions');
        setPermissions({
          system_functions: {},
          sidebar_access: {}
        });
      }
    } catch (err) {
      console.error('❌ Error fetching permissions:', err);
      console.error('❌ Error response:', err.response?.data);
      // Default to no permissions on error
      setPermissions({
        system_functions: {},
        sidebar_access: {}
      });
    }
  };

  // Prepare chart data
  const userDistributionData = [
    { label: 'Users', value: stats.user, color: '#f97316' },
    { label: 'Doctors', value: stats.doctor, color: '#14b8a6' },
    { label: 'Hospitals', value: stats.hospital, color: '#3b82f6' },
    { label: 'Clinics', value: stats.clinic, color: '#8b5cf6' },
    { label: 'Admins', value: stats.admin, color: '#ef4444' },
    { label: 'Super Admins', value: stats.superadmin, color: '#6366f1' },
  ].filter(item => item.value > 0);

  const performanceData = [
    { label: 'Users', value: stats.user, color: '#f97316' },
    { label: 'Doctors', value: stats.doctor, color: '#14b8a6' },
    { label: 'Hospitals', value: stats.hospital, color: '#3b82f6' },
    { label: 'Clinics', value: stats.clinic, color: '#8b5cf6' },
    { label: 'Admins', value: stats.admin, color: '#ef4444' },
    { label: 'Super Admins', value: stats.superadmin, color: '#6366f1' },
  ].filter(item => item.value > 0); // Only show roles that have users

  const sessionData = [
    { label: 'Doctors', value: stats.doctor, color: '#14b8a6' },
    { label: 'Users', value: stats.user, color: '#f97316' },
  ];

  const doctorUserData = [
    { label: 'Users', value: stats.user, color: '#f97316' },
    { label: 'Doctors', value: stats.doctor, color: '#14b8a6' },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-medical-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex items-center justify-between"
      >
        <div>
          <h2 className="text-2xl font-bold text-slate-900">Analytics Dashboard</h2>
          <p className="text-slate-600 text-sm mt-1">Real-time insights and system overview</p>
        </div>
      </motion.div>

      {/* Key Metrics Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-gradient-to-r from-medical-500 to-medical-600 rounded-lg shadow-lg p-6 text-white col-span-1 md:col-span-2"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-white/80 text-sm font-medium mb-1">Total Users</p>
              <h3 className="text-4xl font-bold">{stats.total}</h3>
              <p className="text-white/70 text-xs mt-2">Across all platforms</p>
            </div>
            <div className="w-16 h-16 bg-white/20 rounded-lg flex items-center justify-center backdrop-blur-sm">
              <Users className="w-8 h-8 text-white" />
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
          className="bg-gradient-to-r from-green-500 to-green-600 rounded-lg shadow-lg p-6 text-white"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-white/80 text-sm font-medium mb-1">System Health</p>
              <h3 className="text-2xl font-bold">{analytics.systemUptime}</h3>
              <p className="text-white/70 text-xs mt-2">Uptime</p>
            </div>
            <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center">
              <BarChart3 className="w-6 h-6 text-white" />
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-gradient-to-r from-purple-500 to-purple-600 rounded-lg shadow-lg p-6 text-white"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-white/80 text-sm font-medium mb-1">Revenue</p>
              <h3 className="text-2xl font-bold">${analytics.totalRevenue.toLocaleString()}</h3>
              <p className="text-white/70 text-xs mt-2">This month</p>
            </div>
            <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center">
              <TrendingUp className="w-6 h-6 text-white" />
            </div>
          </div>
        </motion.div>
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* User Distribution Pie Chart */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <CustomPieChart 
            data={userDistributionData} 
            title="User Distribution by Role" 
          />
        </motion.div>

        {/* Performance Bar Chart */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.35 }}
        >
          <CustomBarChart 
            data={performanceData} 
            title="User Count by Role" 
          />
        </motion.div>
      </div>

      {/* Analytics Cards with Mini Charts */}
      <div>
        <h3 className="text-lg font-semibold text-slate-900 mb-4">Quick Analytics</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="bg-white rounded-lg shadow-sm border border-slate-200 p-5"
          >
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-semibold text-slate-900">User Analytics</h4>
              <span className="text-xs font-medium px-2 py-1 rounded-full bg-green-100 text-green-700">
                Total Users: {stats.user}
              </span>
            </div>
            <CustomDonutChart 
              data={sessionData}
              centerText={`${stats.user + stats.doctor}`}
            />
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.45 }}
            className="bg-white rounded-lg shadow-sm border border-slate-200 p-5"
          >
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-semibold text-slate-900">Doctor Analytics</h4>
              <span className="text-xs font-medium px-2 py-1 rounded-full bg-blue-100 text-blue-700">
                Total Doctors: {stats.doctor}
              </span>
            </div>
            <CustomDonutChart 
              data={[
                { label: 'Doctors', value: stats.doctor, color: '#14b8a6' },
                { label: 'Users', value: stats.user, color: '#f97316' },
              ]}
              centerText={`${stats.doctor}`}
            />
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="bg-white rounded-lg shadow-sm border border-slate-200 p-5"
          >
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-semibold text-slate-900">Platform Overview</h4>
              <span className="text-xs font-medium px-2 py-1 rounded-full bg-purple-100 text-purple-700">
                Total: {stats.user + stats.doctor}
              </span>
            </div>
            <CustomDonutChart 
              data={doctorUserData}
              centerText={`${((stats.doctor / (stats.doctor + stats.user)) * 100).toFixed(0)}%`}
            />
          </motion.div>
        </div>
      </div>

      {/* System Functions - Only show if at least one function is accessible */}
      {onNavigate && Object.values(permissions.system_functions).some(val => val === true) && (
        <div>
          <h3 className="text-lg font-semibold text-slate-900 mb-4">System Functions</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <>
              {permissions.system_functions.add_admin && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                  className="bg-white rounded-lg shadow-sm border border-slate-200 p-6 hover:shadow-md transition-all cursor-pointer group"
                  onClick={() => onNavigate('add_admin')}
                >
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-medical-100 rounded-lg flex items-center justify-center group-hover:bg-medical-200 transition-colors">
                      <UserPlus className="w-6 h-6 text-medical-600" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-slate-900 mb-1">Add Admin</h4>
                      <p className="text-sm text-slate-600">Create new administrator accounts</p>
                    </div>
                  </div>
                </motion.div>
              )}

              {permissions.system_functions.setup && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.35 }}
                  className="bg-white rounded-lg shadow-sm border border-slate-200 p-6 hover:shadow-md transition-all cursor-pointer group"
                  onClick={() => onNavigate('setup')}
                >
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-slate-100 rounded-lg flex items-center justify-center group-hover:bg-slate-200 transition-colors">
                      <Settings className="w-6 h-6 text-slate-600" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-slate-900 mb-1">System Setup</h4>
                      <p className="text-sm text-slate-600">Configure system settings and parameters</p>
                    </div>
                  </div>
                </motion.div>
              )}


              {permissions.system_functions.notifications && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 }}
                  className="bg-white rounded-lg shadow-sm border border-slate-200 p-6 hover:shadow-md transition-all cursor-pointer group"
                  onClick={() => onNavigate('notifications')}
                >
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center group-hover:bg-blue-200 transition-colors">
                      <Bell className="w-6 h-6 text-blue-600" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-slate-900 mb-1">Notifications</h4>
                      <p className="text-sm text-slate-600">Manage system notifications and alerts</p>
                    </div>
                  </div>
                </motion.div>
              )}

              {permissions.system_functions.discounts && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5 }}
                  className="bg-white rounded-lg shadow-sm border border-slate-200 p-6 hover:shadow-md transition-all cursor-pointer group"
                  onClick={() => onNavigate('discounts')}
                >
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center group-hover:bg-orange-200 transition-colors">
                      <TrendingUp className="w-6 h-6 text-orange-600" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-slate-900 mb-1">Discount Management</h4>
                      <p className="text-sm text-slate-600">Create and manage package discounts</p>
                    </div>
                  </div>
                </motion.div>
              )}
            </>
          </div>
        </div>
      )}
    </div>
  );
}
